/**
 * An opt-in trace of every write to the hanging-protocol step atoms and the viewport buckets.
 *
 * This exists because a stack trace cannot answer "who wrote this". Recoil applies a `set` updater
 * lazily inside its own batch, so by the time one of these writers runs, the calling component's
 * frame is gone -- an `Error().stack` captured here contains `mySet`, `selectorSet`, `setNodeValue`
 * and nothing above them. That was measured twice while chasing VX-5091, and both times the trail
 * ended in Recoil internals. So the writers name *themselves* instead, and the intent travels as an
 * argument.
 *
 * Kept in the app deliberately rather than removed with the investigation. Four of the SHP bug
 * families to date (VX-4624, VX-4732, VX-4768, VX-4772, then VX-5091) were "the right value written
 * to the wrong place, or the wrong value written to the right place", and every one of them cost a
 * round of ad-hoc `console.log` in these exact functions. Having the trail already there, named by
 * intent, is what turns that into one run.
 *
 * **Off unless asked for**, because these writers run on every viewport edit and every step
 * navigation. Two switches, and they are independent:
 *
 * ```js
 * // From the browser console, persists across reloads for this origin:
 * window.__SIRONA_HP_TRACE__(true);
 * // Or, before the app boots (what the e2e suite uses):
 * localStorage.setItem('srna.trace.hp', 'true');
 * ```
 *
 * **Writes to the console directly, not through `modules/logger`.** That is a deliberate exception to
 * the house rule, and it is forced: `Logger#log` only reaches the console when remote logging is
 * *off* (`env.TEST !== 'true' && !this.#remoteLogging`), and remote logging is on for every build
 * whose `DEPLOY_ENV` is not `local`. So on a PR preview or on deployed dev a `logger` call goes to
 * Datadog and nowhere a developer or a Playwright console listener can see it -- measured: this
 * module logged through `logger.info` first and captured exactly zero lines on a preview run. A trace
 * whose entire purpose is the browser console cannot use a transport that suppresses the console.
 *
 * `console.debug`, with the `no-console` rule disabled for that one line. The rule allows only `warn`
 * and `error`, and this is neither -- calling it a warning would put a trace line into whatever a
 * reader has warnings wired up to, and it would read as a problem in a Datadog session or a bug
 * report. `debug` is what it is, and the exception is narrower than mislabelling every line.
 *
 * Note `debug` is filtered out of Chrome's console by default: the level selector has to include
 * Verbose. That is the right trade for something off by default and aimed at whoever switched it on.
 *
 * ## Reading the trail back, rather than out of the console
 *
 * Every line also lands in a bounded in-page ring buffer, readable with
 * `window.__SIRONA_HP_TRACE_DUMP__()`. That exists because the console is the wrong instrument for
 * the question these traces are usually asked: `HP_TRACE_ECHO=true` on one stepped-HP spec emits
 * ~1700 lines, and the interesting window is the handful between two test actions. A spec can
 * bracket exactly that window instead:
 *
 * ```ts
 * await page.evaluate(() => window.__SIRONA_HP_TRACE_CLEAR__());
 * await viewer0.studiesBar.dragToViewport(index, viewportId);   // the action under suspicion
 * const trail = await page.evaluate(() => window.__SIRONA_HP_TRACE_DUMP__('baseline'));
 * ```
 *
 * `seq` and `t` are on every line for the same reason. Recoil applies `set` updaters lazily inside
 * its own batch, so the order writes *appear* in is not the order they were requested, and a bug
 * whose cause is "the baseline advanced while the step was still clean, and the reader's edit
 * arrived afterwards" is invisible without a total order over the writes. `seq` is that order; `t`
 * (ms since the first traced write) is what separates "same batch" from "a network round trip
 * apart", which no sequence number can.
 */
import { getViewerWindowIdFromUrl } from 'utils/viewerUrlUtils';

const TRACE_STORAGE_KEY = 'srna.trace.hp';

/**
 * How many lines the ring buffer keeps. A stepped-HP spec emits ~1700 across a whole file, so this
 * holds a comfortable majority of one and cannot grow without bound on a long clinical session.
 */
const TRACE_BUFFER_LIMIT = 4000;

/** `null` until first asked, so a disabled trace costs one boolean check per write. */
let isTracingEnabled: boolean | null = null;

/** Total order over traced writes. See the ring-buffer note above for why a count is not enough. */
let traceSequence = 0;

/** Wall clock of the first traced write, so `t` is relative to the trace rather than to the epoch. */
let traceStartedAt: number | null = null;

/** Most recent lines, oldest first, capped at `TRACE_BUFFER_LIMIT`. */
const traceBuffer: string[] = [];

/**
 * Which viewer window this trace belongs to, read from the URL once.
 *
 * Load-bearing for anything cross-window, and its absence cost a full diagnostic round: the step
 * atoms and the baseline are BroadcastChannel-synced, so a run captures two interleaved trails with
 * two independent `seq` counters and two independent `t` origins. Read as one stream that is not
 * merely confusing, it is actively misleading -- `steps=2 baselineSteps=1` looks like a writer that
 * dropped a step when it is simply the other window, which has not learned about it yet.
 *
 * Derived rather than passed in, because every writer would otherwise need a window argument
 * threaded to it, and the one thing they all already share is the page they run on.
 */
function readWindowId(): string {
  try {
    // Through the route utility rather than a regex of our own, so this keeps working if the viewer
    // paths move -- `AppRoutePath` is the one place that knows them. Anything that is not a viewer
    // window reports `-`, the pixel-worker bootstrap window included, which is the honest answer:
    // it has no viewport grid and no step bar.
    return getViewerWindowIdFromUrl(globalThis.location?.href ?? '') ?? '-';
  } catch {
    return '-';
  }
}

let windowId: string | null = null;

function readTracingPreference(): boolean {
  try {
    return globalThis.localStorage?.getItem(TRACE_STORAGE_KEY) === 'true';
  } catch {
    // A storage-denied context (some embeds, some privacy modes) is not a reason to throw from a
    // write path.
    return false;
  }
}

/** Turns the trace on or off for this origin, and remembers it across reloads. */
export function setHPMutationTracing(enabled: boolean): void {
  isTracingEnabled = enabled;
  try {
    if (enabled) globalThis.localStorage?.setItem(TRACE_STORAGE_KEY, 'true');
    else globalThis.localStorage?.removeItem(TRACE_STORAGE_KEY);
  } catch {
    // In-memory only, then. The switch still works for this page.
  }
}

export function isHPMutationTracingEnabled(): boolean {
  isTracingEnabled ??= readTracingPreference();
  return isTracingEnabled;
}

export type TraceDetail = Record<string, unknown>;

/**
 * Records one write, named by the intent that made it.
 *
 * **Pass a thunk when the detail is not free to compute.** A `Record` argument is evaluated by the
 * caller before this function can check whether tracing is on, so `traceHPMutation('x', { d:
 * digestForTrace(steps) })` does the stringify and the hash on every write in production with the
 * trace switched off. These writers run on every viewport edit and every step navigation, which is
 * exactly the cost the "off unless asked for" note above promises not to pay. A `() => Record`
 * defers the whole thing behind the enabled check, so a disabled trace really is one boolean.
 *
 * Cheap detail -- counts, ids, booleans -- can stay an object literal; there is nothing to defer.
 *
 * `detail` is flattened into `key=value` pairs rather than logged as an object, because the browser
 * console elides object contents when a headless run captures it -- an object-shaped log reads as
 * `{bucket: __flat__, nulled: Array(2)}` in Playwright output, which is exactly the part you need.
 * Arrays are joined; anything else goes through `String`.
 */
export function traceHPMutation(
  intent: string,
  detail: TraceDetail | (() => TraceDetail) = {}
): void {
  if (!isHPMutationTracingEnabled()) return;
  const resolved = typeof detail === 'function' ? detail() : detail;
  const pairs = Object.entries(resolved).map(([key, value]) => {
    if (Array.isArray(value)) return `${key}=[${value.map(String).join(',')}]`;
    return `${key}=${value == null ? 'null' : String(value)}`;
  });

  const now = Date.now();
  traceStartedAt ??= now;
  windowId ??= readWindowId();
  traceSequence += 1;
  // `at` is wall clock, `t` is relative to this window's first traced write. Both, because they
  // answer different questions and using the wrong one is a trap this investigation fell into: `t`
  // reads naturally when following a single window, but each window has its own origin, so
  // comparing `t` across windows silently compares two different clocks. Any cross-window ordering
  // has to be read off `at`. Same-machine windows share the OS clock, so `at` is directly
  // comparable.
  const line =
    `w${windowId}#${traceSequence} at=${now} t=${now - traceStartedAt}ms ${intent}` +
    `${pairs.length > 0 ? ` ${pairs.join(' ')}` : ''}`;

  traceBuffer.push(line);
  // Drop from the front rather than slicing: this runs on every viewport edit, and a shift on a
  // bounded array is cheaper than reallocating one.
  if (traceBuffer.length > TRACE_BUFFER_LIMIT) traceBuffer.shift();

  // eslint-disable-next-line no-console
  console.debug(`[HP:trace] ${line}`);
}

/**
 * A short, stable digest of any JSON-serialisable value, for trace lines that need to say "this
 * changed" without printing the thing that changed.
 *
 * The step atoms and the baseline are arrays of whole `StepConfiguration`s, so printing them is not
 * an option -- one write is kilobytes, and the trace runs on every viewport edit. A length is what
 * the baseline trace printed instead, and a length cannot answer the question the VX-5124
 * investigation actually has ("did this write change *this step's* viewports?"), because the
 * count is identical either way.
 *
 * FNV-1a, not a cryptographic hash: this is only ever compared against another digest from the same
 * build in the same session, so collision resistance buys nothing and eight hex characters keep a
 * trace line readable.
 */
export function digestForTrace(value: unknown): string {
  let json: string;
  try {
    json = JSON.stringify(value) ?? 'undefined';
  } catch {
    // A cyclic or unserialisable value is not a reason to throw from a write path. It also cannot be
    // compared, so say so rather than emitting a digest that looks meaningful.
    return 'unserializable';
  }

  let hash = 0x811c9dc5;
  for (let index = 0; index < json.length; index += 1) {
    hash ^= json.charCodeAt(index);
    // >>> 0 keeps this an unsigned 32-bit multiply; Math.imul alone would let it go negative.
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash.toString(16).padStart(8, '0');
}

/**
 * Names a point in a test or a manual repro, so the writes on either side of it can be told apart.
 *
 * Traced like any other line, and deliberately so: the question is always "what ran *between* these
 * two things I did", and an intent named by the operator is the only marker the app itself cannot
 * emit. Unlike `__SIRONA_HP_TRACE_CLEAR__` it keeps the preceding trail, which matters when the
 * cause turns out to precede the action you suspected.
 */
export function markHPTraceCheckpoint(label: string): void {
  traceHPMutation('checkpoint', { label });
}

/**
 * The buffered trail, oldest first, optionally narrowed to lines containing `filter`.
 *
 * Returns a copy: a caller iterating this while the viewer keeps writing must not see the ring
 * buffer shift underneath it.
 */
export function dumpHPMutationTrace(filter?: string): string[] {
  const lines = [...traceBuffer];
  if (filter == null || filter === '') return lines;
  return lines.filter((line) => line.includes(filter));
}

/**
 * Drops the buffered trail. `seq` deliberately keeps counting, so a dump taken after a clear can
 * still be placed relative to one taken before it.
 */
export function clearHPMutationTrace(): void {
  traceBuffer.length = 0;
}

/**
 * Publishes the console switch. Called once from the viewer's entry so the name exists before anyone
 * needs it -- a switch you have to import is a switch nobody finds at 2am.
 */
export function installHPMutationTraceSwitch(): void {
  const globals = globalThis as unknown as Record<string, unknown>;
  globals.__SIRONA_HP_TRACE__ = (enabled: boolean = true) => {
    setHPMutationTracing(enabled);
    return `[HP:trace] ${enabled ? 'on' : 'off'}`;
  };
  // Published alongside the switch for the same reason it is: a spec reaches these through
  // `page.evaluate`, which has no module scope to import from.
  globals.__SIRONA_HP_TRACE_DUMP__ = (filter?: string) => dumpHPMutationTrace(filter);
  globals.__SIRONA_HP_TRACE_CLEAR__ = () => {
    clearHPMutationTrace();
    return '[HP:trace] cleared';
  };
  globals.__SIRONA_HP_TRACE_MARK__ = (label: string) => {
    markHPTraceCheckpoint(label);
    return `[HP:trace] ${label}`;
  };
}
